import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "../api/client";
import { keys, useLocations } from "../api/hooks";
import { adminSystemSchema, type AdminEvent } from "../../api/contracts";
import { formatInstant } from "../lib/format";
import { ErrorNote, PageHeader, Panel, Spinner, Stat } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { StatusDot } from "../components/StatusDot";
import { Timestamp } from "../components/Timestamp";

export function AdminOverviewPage() {
  const system = useQuery({ queryKey: keys.admin.system, queryFn: () => api("/api/admin/system", adminSystemSchema), refetchInterval: 10_000 });
  const locations = useLocations();
  if (system.isPending) return <Spinner />;
  if (system.isError) return <ErrorNote error={system.error} />;
  const s = system.data;
  const nameOf = (blvdId: string | null) => (blvdId && locations.data?.find((l) => l.blvdId === blvdId)?.name) || "—";
  const columns: Column<AdminEvent>[] = [
    { key: "event", header: "Event", cell: (e) => e.eventType },
    { key: "location", header: "Location", cell: (e) => nameOf(e.blvdId) },
    { key: "received", header: "Received", align: "right", width: "9rem", cell: (e) => <Timestamp at={e.receivedAt} /> },
  ];
  return (
    <>
      <PageHeader title="Overview" subtitle="Where BTB blocks stand across every location." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Mode" value={<StatusDot tone={s.mode === "live" ? "success" : "neutral"} label={s.mode === "live" ? "Live" : "Preview"} />} />
        <Stat label="Corporate pause"
          value={s.globalPaused ? <StatusDot tone="warning" wrap label={`Paused by ${s.pausedBy}`} detail={s.pausedAt ? formatInstant(s.pausedAt) : undefined} /> : <StatusDot tone="success" label="Running" />}
          detail={<Link to="/admin/system" className="text-hs-deep hover:underline">Change in System</Link>} />
        <Stat label="Locations on" value={s.enabledLocations} />
        <Stat label="Jobs queued" value={s.queue.queued} detail={`${s.queue.running} running · ${s.queue.failed} failed`} />
      </div>
      <Panel className="mt-4" title="Recent Boulevard deliveries" padding="none">
        {s.recentEvents.length === 0
          ? <p className="p-4 text-[13px] text-hs-text-muted">None yet.</p>
          : <DataTable columns={columns} rows={s.recentEvents} rowKey={(e) => e.id} density="compact" caption="Recent Boulevard deliveries" />}
      </Panel>
    </>
  );
}
